import React from "react";
import Script from "next/script";
import { Hooks } from "@xt/xtbase";
const { useIntl } = Hooks;
import { Context } from "@xt/xtbase";
const { XtContext } = Context;
import { get_captchaCurrentPlatform, post_captchaApply, post_captchaValidate } from "api/xt-app";
import { message } from "antd";
import { getHosts } from "utils/method";
import sessionStorage from "utils/sessionStorage";

const CAPTCHA_TYPE_GEETEST = 2;
const CAPTCHA_TYPE_GOOGLE = 3;
const CAPTCHA_TYPE_TURNSTILE = 4;

const GOOGLE_RECAPTCHA_SCRIPT_BASE = "https://www.google.com/recaptcha/api.js?render=explicit";
const TURNSTILE_SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js";

/** 项目 langCode 映射到 reCAPTCHA 语言代码 (hl)，见 https://developers.google.com/recaptcha/docs/language；根据选择的语言传对应参数 */
const RECAPTCHA_HL_MAP = {
  cn: "zh-CN",
  hk: "zh-TW",
  en: "en",
  eng: "en",
  pt: "pt",
  ar: "ar",
  vi: "vi",
  ja: "ja",
  ko: "ko",
  in: "id",
  ru: "ru",
  es: "es",
  fr: "fr",
  de: "de",
};
function getRecaptchaScriptUrl(langCode) {
  const hl = RECAPTCHA_HL_MAP[langCode] || "en";
  return `${GOOGLE_RECAPTCHA_SCRIPT_BASE}&hl=${hl}`;
}

/** 项目 langCode 映射到 Turnstile 语言代码，与 RECAPTCHA_HL_MAP 一致，见 https://developers.cloudflare.com/turnstile/reference/supported-languages/ */
function getTurnstileLanguage(langCode) {
  return RECAPTCHA_HL_MAP[langCode] || "en";
}

// Google / Turnstile 的 key 放前端，不从 apply 接口获取；仅极验会调 apply
const GOOGLE_RECAPTCHA_SITEKEY = "6Le6D3gsAAAAAGut0EBoQvpZsw9fKkJLpptUSpAm";
const TURNSTILE_SITEKEY = "0x4AAAAAACilX33Vb22AUIpo";

const captcha = {};

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") return reject(new Error("no window"));
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) return resolve();
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

export default React.forwardRef(function Geetest(props, ref) {
  const intl = useIntl();
  const [appState] = React.useContext(XtContext);
  const googleContainerRef = React.useRef(null);
  const turnstileContainerRef = React.useRef(null);
  /** 登录按钮触发的 verify 在验证码未就绪时挂起，初始化完成后自动执行一次 */
  const pendingVerifyRef = React.useRef(false);
  /** 当前验证码类型，用于 Google 时在登录按钮上方直接展示 */
  const [captchaType, setCaptchaType] = React.useState(null);
  /** Google 可见模式：用户勾选后得到的 certificate，在点击登录时交给父组件 */
  const googleCertificateRef = React.useRef(null);
  const googleWidgetIdRef = React.useRef(null);
  /** Turnstile 可见模式：用户完成验证后得到的 certificate */
  const turnstileCertificateRef = React.useRef(null);
  const turnstileWidgetIdRef = React.useRef(null);

  const [state_func, setState_func] = React.useState();
  React.useEffect(() => {
    if (!state_func || !state_func.func) return;
    const { func, data } = state_func;
    props[func] && props[func](data);
  }, [state_func]);

  const verify = () => {
    if (captcha.offline) return setState_func({ func: "onSuccess" });
    const puzzleValidateString = sessionStorage.get("puzzleValidateString");
    if (puzzleValidateString) {
      setState_func({ func: "onSuccess", data: puzzleValidateString });
      sessionStorage.remove("puzzleValidateString");
      return;
    }
    if (captcha.type === CAPTCHA_TYPE_GOOGLE && captcha.googleVisible) {
      if (googleCertificateRef.current) {
        const cert = googleCertificateRef.current;
        googleCertificateRef.current = null;
        return setState_func({ func: "onSuccess", data: cert });
      }
      message.error(intl.formatMessage({ id: "register.geetestError" }) || "Please complete the verification first");
      return;
    }
    if (captcha.type === CAPTCHA_TYPE_TURNSTILE && captcha.turnstileVisible) {
      if (turnstileCertificateRef.current) {
        const cert = turnstileCertificateRef.current;
        turnstileCertificateRef.current = null;
        return setState_func({ func: "onSuccess", data: cert });
      }
      message.error(intl.formatMessage({ id: "register.geetestError" }) || "Please complete the verification first");
      return;
    }
    if (captcha.instance && captcha.instance.showCaptcha) {
      return captcha.instance.showCaptcha();
    }
    pendingVerifyRef.current = true;
  };

  const reset = () => {
    if (captcha.offline) return;
    if (captcha.type === CAPTCHA_TYPE_TURNSTILE && captcha.turnstileVisible) {
      turnstileCertificateRef.current = null;
      if (window.turnstile && turnstileWidgetIdRef.current != null) {
        try {
          window.turnstile.reset(turnstileWidgetIdRef.current);
        } catch (e) {}
      }
      return;
    }
    if (captcha.instance && captcha.instance.reset) return captcha.instance.reset();
    console.error("【验证码重置失败】%o", captcha.instance);
  };

  const destroy = () => {
    if (captcha.offline) return;
    if (captcha.type === CAPTCHA_TYPE_TURNSTILE && captcha.turnstileVisible) {
      turnstileCertificateRef.current = null;
      turnstileWidgetIdRef.current = null;
      const container = turnstileContainerRef.current || document.getElementById("geetest-turnstile");
      if (container && container.innerHTML) container.innerHTML = "";
      return props.onDestroy && props.onDestroy();
    }
    if (captcha.instance && captcha.instance.destroy) {
      captcha.instance.destroy();
      return props.onDestroy && props.onDestroy();
    }
    console.error("【验证码销毁失败】%o", captcha.instance);
  };

  React.useEffect(() => {
    let cancelled = false;
    captcha.instance = null;
    captcha.type = null;
    googleWidgetIdRef.current = null;
    googleCertificateRef.current = null;
    turnstileWidgetIdRef.current = null;
    turnstileCertificateRef.current = null;

    get_captchaCurrentPlatform()
      .then((platformRes) => {
        if (cancelled) return null;
        const currentType = platformRes ?? CAPTCHA_TYPE_GEETEST;
        // 仅 type 为 2（极验）时需要调 apply 看 enabled 并拿 code/sdkKey
        if (currentType === CAPTCHA_TYPE_GEETEST) {
          return post_captchaApply({ params: { type: currentType } }).then((applyRes) => {
            const applyData = applyRes?.data ?? applyRes;
            return { ...applyData, type: currentType };
          });
        }
        // type 3/4 不调 apply，直接用 current-platform 返回做后续初始化
        return Promise.resolve({ type: currentType });
      })
      .then((data) => {
        if (cancelled || !data) return;
        const { type = CAPTCHA_TYPE_GEETEST, code, sdkKey, enabled } = data;
        captcha.type = type;
        captcha.googleVisible = type === CAPTCHA_TYPE_GOOGLE;
        captcha.turnstileVisible = type === CAPTCHA_TYPE_TURNSTILE;
        setCaptchaType(type);

        // 仅极验(type=2) 需要根据 apply 的 enabled 决定是否离线
        if (type === CAPTCHA_TYPE_GEETEST) {
          if (!enabled) {
            captcha.offline = true;
            return setState_func({ func: "onOffline" });
          }
        }
        captcha.offline = false;

        if (type === CAPTCHA_TYPE_GOOGLE) {
          captcha.googleVisible = true;
          const recaptchaScriptUrl = getRecaptchaScriptUrl(appState?.langCode);
          const isDark = (appState?.theme || "").toLowerCase() === "dark";
          loadScript(recaptchaScriptUrl)
            .then(() => {
              if (cancelled || typeof window.grecaptcha === "undefined") return;
              const sitekey = GOOGLE_RECAPTCHA_SITEKEY;
              if (!sitekey) {
                console.error("【Google reCAPTCHA】缺少 sitekey");
                return;
              }
              let googleRetryCount = 0;
              const GOOGLE_RETRY_MAX = 40;
              const tryRenderGoogle = () => {
                if (cancelled) return;
                // Already rendered for this mount (Strict Mode / ready double-fire)
                if (googleWidgetIdRef.current != null) return;
                const container = googleContainerRef.current || document.getElementById("geetest-google");
                if (!container) {
                  if (googleRetryCount < GOOGLE_RETRY_MAX) {
                    googleRetryCount += 1;
                    setTimeout(tryRenderGoogle, 50);
                  }
                  return;
                }
                // Container still holds a previous widget — clear before re-render
                if (container.childNodes.length > 0) {
                  try {
                    container.innerHTML = "";
                  } catch (e) {}
                }
                let widgetId;
                try {
                  widgetId = window.grecaptcha.render(container, {
                    sitekey,
                    size: "normal",
                    theme: isDark ? "dark" : "light",
                    callback: (token) => {
                      post_captchaValidate({
                        params: { type: CAPTCHA_TYPE_GOOGLE, data: token },
                      })
                        .then((res) => {
                          googleCertificateRef.current = res.certificate;
                        })
                        .catch((e) => {
                          console.error("【Google 验证码服务端校验失败】", e);
                          reset();
                        });
                    },
                    "expired-callback": () => {
                      googleCertificateRef.current = null;
                    },
                  });
                } catch (e) {
                  // "already been rendered in this element" — treat as non-fatal
                  console.warn("【Google reCAPTCHA render skipped】", e?.message || e);
                  return;
                }
                if (cancelled) {
                  try {
                    container.innerHTML = "";
                  } catch (e) {}
                  return;
                }
                googleWidgetIdRef.current = widgetId;
                captcha.instance = {
                  showCaptcha: () => {},
                  reset: () => {
                    googleCertificateRef.current = null;
                    if (window.grecaptcha && googleWidgetIdRef.current != null) {
                      try {
                        window.grecaptcha.reset(googleWidgetIdRef.current);
                      } catch (e) {}
                    }
                  },
                  destroy: () => {
                    googleCertificateRef.current = null;
                    googleWidgetIdRef.current = null;
                    try {
                      if (container && container.innerHTML) container.innerHTML = "";
                    } catch (e) {}
                  },
                };
              };
              window.grecaptcha.ready(tryRenderGoogle);
            })
            .catch((e) => {
              console.error("【Google reCAPTCHA 脚本加载失败】", e);
            });
          return;
        }

        if (type === CAPTCHA_TYPE_TURNSTILE) {
          loadScript(TURNSTILE_SCRIPT)
            .then(() => {
              if (cancelled || typeof window.turnstile === "undefined") return;
              const sitekey = TURNSTILE_SITEKEY;
              if (!sitekey) {
                console.error("【Cloudflare Turnstile】缺少 sitekey");
                return;
              }
              let turnstileRetryCount = 0;
              const TURNSTILE_RETRY_MAX = 40;
              const tryRenderTurnstile = () => {
                if (cancelled) return;
                if (turnstileWidgetIdRef.current != null) return;
                const container = turnstileContainerRef.current || document.getElementById("geetest-turnstile");
                if (!container) {
                  if (turnstileRetryCount < TURNSTILE_RETRY_MAX) {
                    turnstileRetryCount += 1;
                    setTimeout(tryRenderTurnstile, 50);
                  }
                  return;
                }
                if (container.childNodes.length > 0) {
                  try {
                    container.innerHTML = "";
                  } catch (e) {}
                }
                let widgetId;
                try {
                  widgetId = window.turnstile.render(container, {
                    sitekey,
                    size: "normal",
                    theme: (appState?.theme || "").toLowerCase() === "dark" ? "dark" : "light",
                    language: getTurnstileLanguage(appState?.langCode),
                    callback: (token) => {
                      post_captchaValidate({
                        params: { type: CAPTCHA_TYPE_TURNSTILE, data: token },
                      })
                        .then((res) => {
                          turnstileCertificateRef.current = res.certificate;
                        })
                        .catch((e) => {
                          console.error("【Turnstile 服务端校验失败】", e);
                          reset();
                        });
                    },
                    "expired-callback": () => {
                      turnstileCertificateRef.current = null;
                    },
                  });
                } catch (e) {
                  console.warn("【Turnstile render skipped】", e?.message || e);
                  return;
                }
                if (cancelled) {
                  try {
                    container.innerHTML = "";
                  } catch (e) {}
                  return;
                }
                turnstileWidgetIdRef.current = widgetId;
                captcha.instance = {
                  showCaptcha: () => {},
                  reset: () => {
                    turnstileCertificateRef.current = null;
                    if (window.turnstile && turnstileWidgetIdRef.current != null) {
                      try {
                        window.turnstile.reset(turnstileWidgetIdRef.current);
                      } catch (e) {}
                    }
                  },
                  destroy: () => {
                    turnstileCertificateRef.current = null;
                    turnstileWidgetIdRef.current = null;
                    try {
                      if (container && container.innerHTML) container.innerHTML = "";
                    } catch (e) {}
                  },
                };
                if (pendingVerifyRef.current) {
                  pendingVerifyRef.current = false;
                }
              };
              tryRenderTurnstile();
            })
            .catch((e) => {
              console.error("【Turnstile 脚本加载失败】", e);
            });
          return;
        }

        if (type === CAPTCHA_TYPE_GEETEST) {
          const { host, protocol } = getHosts();
          const config = {
            captchaId: sdkKey,
            product: props.product || "bind",
            /** 极验 4 语言：根据当前选择的语言传对应参数 */
            language: (() => {
              const obj = {
                cn: "zho",
                hk: "zho-hk",
                en: "eng",
                pt: "por",
                ar: "ara",
                ja: "jpn",
                in: "ind",
                ru: "rus",
                es: "spa",
              };
              const lang = appState?.langCode;
              return obj[lang] || "eng";
            })(),
            protocol: protocol + "//",
            apiServers: [host + "/geapi/v4"],
          };
          try {
            require("./gt4");
            initGeetest4(config, (captchaObj) => {
              if (cancelled) {
                try {
                  captchaObj.destroy && captchaObj.destroy();
                } catch (e) {}
                return;
              }
              captcha.instance = captchaObj;
              captchaObj.appendTo("#geetest");
              if (pendingVerifyRef.current) {
                pendingVerifyRef.current = false;
                if (typeof captchaObj.showCaptcha === "function") captchaObj.showCaptcha();
                else if (typeof captchaObj.verify === "function") captchaObj.verify();
              }
              captchaObj.onSuccess(() => {
                const result = captchaObj.getValidate();
                post_captchaValidate({
                  params: {
                    data: JSON.stringify(result),
                    type: CAPTCHA_TYPE_GEETEST,
                  },
                })
                  .then((res) => {
                    setState_func({ func: "onSuccess", data: res.certificate });
                  })
                  .catch((e) => {
                    console.error("【验证码服务端校验失败】", e);
                    reset();
                  });
              });
            });
          } catch (e) {
            console.warn("【Geetest 捕获错误】", e);
          }
        }
      })
      .catch((e) => {
        if (cancelled) return;
        // message.error(intl.formatMessage({ id: "register.geetestError" }));
        console.error("【验证码初始化失败】", e);
      });

    return () => {
      cancelled = true;
      try {
        if (captcha.instance && captcha.instance.destroy) captcha.instance.destroy();
      } catch (e) {}
      captcha.instance = null;
      googleCertificateRef.current = null;
      googleWidgetIdRef.current = null;
      turnstileCertificateRef.current = null;
      turnstileWidgetIdRef.current = null;
      const googleEl = googleContainerRef.current || document.getElementById("geetest-google");
      const turnstileEl = turnstileContainerRef.current || document.getElementById("geetest-turnstile");
      try {
        if (googleEl) googleEl.innerHTML = "";
        if (turnstileEl) turnstileEl.innerHTML = "";
      } catch (e) {}
    };
  }, []);

  React.useImperativeHandle(ref, () => ({ verify, reset, destroy }));

  const isGoogleVisible = captchaType === CAPTCHA_TYPE_GOOGLE;
  const isTurnstileVisible = captchaType === CAPTCHA_TYPE_TURNSTILE;
  return (
    <>
      <div id={"geetest"} style={{ display: "none" }} />
      <div
        ref={googleContainerRef}
        id={"geetest-google"}
        style={{
          display: isGoogleVisible ? "block" : "none",
          marginBottom: isGoogleVisible ? 16 : 0,
          marginTop: isGoogleVisible ? 16 : 0,
          minHeight: isGoogleVisible ? 78 : 0,
        }}
      />
      <div
        ref={turnstileContainerRef}
        id={"geetest-turnstile"}
        style={{
          display: isTurnstileVisible ? "block" : "none",
          marginBottom: isTurnstileVisible ? 16 : 0,
          marginTop: isTurnstileVisible ? 16 : 0,
          minHeight: isTurnstileVisible ? 65 : 0,
        }}
      />
    </>
  );
});
